import React, { useState, useEffect, useMemo, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Badge } from "./components/ui/badge";
import { Progress } from "./components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";
import { TrendingUp, TrendingDown, DollarSign, PieChart, BarChart3, User, LogOut, CreditCard, Star, Crown, Home, Building, Banknote, Target, AlertCircle, BookOpen, Calendar, Plus, Calculator, List, Edit, Trash2, Filter, Search, X, CheckSquare, Clock, Flag, Sparkles, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Check, CheckCircle, Lock, Camera, Image as ImageIcon, ZoomIn, Users, Lightbulb, Receipt } from "lucide-react";
import GradientProgressBar from "./components/GradientProgressBar";
import MoneyCycleChart from "./components/MoneyCycleChart";
import AlternativeMoneyCycleChart from "./components/AlternativeMoneyCycleChart";
import AnimatedMoneyCycleChart from "./components/AnimatedMoneyCycleChart";
import LadderOfWealthChart from "./components/LadderOfWealthChart";
import CurrencySelector from "./components/CurrencySelector";
import JournalModule from "./components/JournalModule";
import BeliefsModule from "./components/BeliefsModule";
import HabitsModule from "./components/HabitsModule";
import IdentityModule from "./components/IdentityModule";
import MyNumbersModule from "./components/MyNumbersModule";
import VisionModule from "./components/VisionModule";
import AnalyticsModule from "./components/AnalyticsModule";
import SavingsPotentialModule from "./components/SavingsPotentialModule";
import NetWorthChartModule from "./components/NetWorthChartModule";
import PremiumLock from "./components/PremiumLock";
import PremiumUpgradeModal from "./components/PremiumUpgradeModal";
import GoalsModule, { TaskFormModal } from "./components/GoalsModule";
import QuickQuizModule, { QuickQuizStartButton } from "./components/QuickQuizModule";
import UnderstandingExpensesModule, { UnderstandingExpensesStartButton } from "./components/UnderstandingExpensesModule";
import DashboardView from "./components/DashboardView";
import NetWorthDisplayModule from "./components/NetWorthDisplayModule";
import FreedomKPIsModule from "./components/FreedomKPIsModule";
import QuizResultCarousel from "./components/QuizResultCarousel";
import ForecastChart from "./components/ForecastChart";
import DiagnosisFlow from "./components/DiagnosisFlow";
import ExpenseTrackerModule, { ExpenseFormModal } from "./components/ExpenseTrackerModule";
import ActionPlanModule from "./components/ActionPlanModule";
import FamilyManagementModal from "./components/FamilyManagementModal";
import FamilyDashboardWidget from "./components/FamilyDashboardWidget";
import JoinHouseholdPage from "./components/JoinHouseholdPage";
import WealthLadderModule from "./components/WealthLadderModule";
import MoneyCycleModule from "./components/MoneyCycleModule";
import AlternativeMoneyCycleModule from "./components/AlternativeMoneyCycleModule";
import AnimatedMoneyCycleModule from "./components/AnimatedMoneyCycleModule";
import ValueCreationModuleV2 from "./components/ValueCreationModuleV2";
import ScaleValueModule from "./components/ScaleValueModule";
import LanguageSwitcher from "./components/LanguageSwitcher";
import ValueCaptureModule from "./components/ValueCaptureModule";
import WealthLadderEducationalModule from "./components/WealthLadderEducationalModule";
import LevelBadge from "./components/LevelBadge";
import NextActionCard from "./components/NextActionCard";
import TasksList from "./components/TasksList";
import InputModules from "./components/InputModules";
import AdvancedTools from "./components/AdvancedTools";
import DashboardV2 from "./components/DashboardV2";
import SubscriptionSuccess from "./components/SubscriptionSuccess";
import SubscriptionCancel from "./components/SubscriptionCancel";
import PremiumUpgradeButton from "./components/PremiumUpgradeButton";
import { ActionFeedbackProvider, ActionFeedbackInline, useActionFeedback, showFeedbackToast, processEventResponse } from "./components/ActionFeedback";
// Homepage import removed - using HomePage instead
import { useMoneyCycleData } from "./data/moneyCycle.selectors";
import { useLadderStage } from "./data/ladder.selectors";
import MoneyCyclePreview from "./MoneyCyclePreview";
import { SimpleAuthProvider, useAuth } from "./auth/SimpleAuthProvider";
import SimpleLoginComponent from "./components/SimpleLoginComponent";
import AuthCallback from "./components/AuthCallback";
import HomePage from "./components/HomePage";
import UserProfile from "./components/UserProfile";
import { CurrencyProvider, useCurrency } from "./contexts/CurrencyContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";
import CookieBanner from "./components/CookieBanner";
import { TrustSignal } from "./components/ConsentComponents";
import { SubscriptionProvider, useSubscription } from "./contexts/SubscriptionContext";
import { useTranslation } from 'react-i18next';
import { useLanguage } from './contexts/LanguageContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart as RechartsPieChart, Pie, Cell, LineChart, Line, ComposedChart, Area, ReferenceLine } from 'recharts';
import "./App.css";

// Add number formatting helper function
const formatCurrency = (amount, currencyCode = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount);
};

// Handle multiple domains dynamically
const getBackendUrl = () => {
  const hostname = window.location.hostname;
  
  // If on localhost, use local backend
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8001';
  }
  
  // If on prospermind.com, use the same domain for API
  if (hostname === 'prospermind.com') {
    return `https://prospermind.com`;
  }
  
  // If on emergent.host domain, use same domain
  if (hostname.includes('emergent.host')) {
    return `https://${hostname}`;
  }
  
  // Default to environment variable or current domain
  return process.env.REACT_APP_BACKEND_URL || `https://${hostname}`;
};

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// axios defaults - not using withCredentials since we use Bearer token auth
// axios.defaults.withCredentials = true; // Removed - causes CORS issues with credentials

// Global axios interceptor to add auth token to ALL requests
axios.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);


const Dashboard = ({ user, onLogout, setNeedsDiagnosis }) => {
  const { t, i18n } = useTranslation();
  const { canAccessFeature, isPremium, updateSubscription } = useSubscription();
  const [summary, setSummary] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [myNumbers, setMyNumbers] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [income, setIncome] = useState([]);
  const [debt, setDebt] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [budgetExpenses, setBudgetExpenses] = useState([]);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [openExpenseFormDirectly, setOpenExpenseFormDirectly] = useState(false);
  const [newExpense, setNewExpense] = useState({ description: "", amount: "", category: "other", date: new Date().toISOString().split('T')[0], budget_item_id: "" });
  const [newBudgetExpense, setNewBudgetExpense] = useState({ description: "", planned_amount: "", category: "other" });
  const [newIncome, setNewIncome] = useState({ source: "", amount: "", date: new Date().toISOString().split('T')[0] });
  const [newDebt, setNewDebt] = useState({ description: "", amount: "", interest_rate: "", date: new Date().toISOString().split('T')[0] });
  const [newInvestment, setNewInvestment] = useState({ description: "", amount: "", investment_type: "stocks", date: new Date().toISOString().split('T')[0] });
  const [editingExpense, setEditingExpense] = useState(null);
  const [editingIncome, setEditingIncome] = useState(null);
  const [showForecast, setShowForecast] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  // Budget Module State
  const [activeBudgetTab, setActiveBudgetTab] = useState("catalogue");
  const [budgetCatalogueItems, setBudgetCatalogueItems] = useState([]);
  const [monthlyBudgetPlan, setMonthlyBudgetPlan] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [newCatalogueItem, setNewCatalogueItem] = useState({ 
    name: "", 
    type: "expense", // income or expense
    category: "",
    defaultAmount: "" 
  });
  const [newBudgetPlanItem, setNewBudgetPlanItem] = useState({ 
    catalogueItemId: "", 
    customName: "",
    plannedAmount: "", 
    type: "expense" 
  });

  // Quick Quiz State
  const [hasCompletedQuiz, setHasCompletedQuiz] = useState(false);
  const [showQuickQuiz, setShowQuickQuiz] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  const [hasTrackedFirstExpense, setHasTrackedFirstExpense] = useState(true); // Default true to hide banner initially
  
  // Understanding Expenses Module State
  const [showUnderstandingExpenses, setShowUnderstandingExpenses] = useState(false);

  // Expense Options State (for ExpenseTrackerModule)
  const [expenseOptions, setExpenseOptions] = useState({ categories: [], payment_methods: [] });
  const [userProfile, setUserProfile] = useState(null);

  // Family Membership State
  const [showFamilyModal, setShowFamilyModal] = useState(false);
  const [hasFamilySubscription, setHasFamilySubscription] = useState(false);

  // Value Creation Module State
  const [showValueCreationModule, setShowValueCreationModule] = useState(false);

  // Scale Value Module State
  const [showScaleValueModule, setShowScaleValueModule] = useState(false);

  // Value Capture Module State
  const [showValueCaptureModule, setShowValueCaptureModule] = useState(false);

  // Wealth Ladder Educational Module State
  const [showWealthLadderModule, setShowWealthLadderModule] = useState(false);

  // Fetch Quick Quiz status
  useEffect(() => {
    const fetchQuizStatus = async () => {
      try {
        const response = await axios.get(`${API}/quick-quiz/result`);
        if (response.data) {
          setHasCompletedQuiz(response.data.has_completed);
          if (response.data.result) {
            setQuizResult(response.data.result);
          }
        }
      } catch (error) {
        console.error("Error fetching quiz status:", error);
      }
    };
    fetchQuizStatus();
  }, []);
  
  // Fetch user progress to check if first expense has been tracked
  useEffect(() => {
    const fetchUserProgress = async () => {
      try {
        const token = sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken');
        const userId = sessionStorage.getItem('prosperMindUserId');
        if (!userId) return;
        
        const response = await fetch(`${API}/api/gamification/journey/basic?user_id=${userId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        if (response.ok) {
          const data = await response.json();
          setHasTrackedFirstExpense(data.firstExpenseTracked || false);
        }
      } catch (error) {
        console.error("Error fetching user progress:", error);
        setHasTrackedFirstExpense(false);
      }
    };
    fetchUserProgress();
  }, []);

  // Reset openExpenseFormDirectly flag after navigation completes
  useEffect(() => {
    if (openExpenseFormDirectly && activeTab === 'expenses') {
      // Reset flag after component has mounted with the form open
      // Use longer delay to ensure form stays open
      const timer = setTimeout(() => setOpenExpenseFormDirectly(false), 500);
      return () => clearTimeout(timer);
    }
  }, [activeTab]); // Only depend on activeTab, not the flag itself

  // Handle Quick Quiz completion
  const handleQuizComplete = async (result) => {
    try {
      await axios.post(`${API}/quick-quiz/submit`, {
        answers: result.answers || [],
        stage_key: result.stage_key,
        stage_name: result.stage_name,
        stage_level: result.stage_level,
        direction: result.direction
      });
      
      setHasCompletedQuiz(true);
      setQuizResult(result);
      setShowQuickQuiz(false);
      toast.success(`You are a ${result.stage_name}!`);
      
      // Dispatch event to refresh dashboard data from backend
      window.dispatchEvent(new CustomEvent('quizCompleted'));
    } catch (error) {
      console.error("Error saving quiz result:", error);
      toast.error("Failed to save quiz result");
    }
  };

  // Handle Wealth Ladder quiz completion
  const handleWealthLadderComplete = async (result) => {
    try {
      await axios.post(`${API}/wealth-ladder/save-assessment`, {
        current_stage: result.stage_key,
        drivers: result.answers || [],
        calculation_data: { diagnosis_total: result.stage_level || 0 },
        notes: ""
      });

      setShowWealthLadderModule(false);
      toast.success(`Wealth Ladder completed: ${result.stage_name}!`);

      // Refresh dashboard data from backend (updates progressPercent + Next Action)
      window.dispatchEvent(new CustomEvent('quizCompleted'));
    } catch (error) {
      console.error("Error saving Wealth Ladder assessment:", error);
      toast.error("Failed to save Wealth Ladder result");
    }
  };

  // Handle Quick Quiz navigation
  const handleQuizNavigate = (destination) => {
    setShowQuickQuiz(false);
    if (destination === 'goals') {
      setActiveTab('goals');
    }
  };

  // Fetch user profile to get subscription info
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await axios.get(`${API}/user/profile`);
        if (response.data) {
          updateSubscription(response.data.subscription_tier, response.data.subscription_expires);
          setUserProfile(response.data);
          // Check for family subscription
          const isFamilyPlan = response.data.subscription_tier === 'premium_family' || 
                               response.data.subscription === 'premium_family';
          setHasFamilySubscription(isFamilyPlan);
        }
      } catch (error) {
        console.error("Error fetching user profile for subscription:", error);
      }
    };
    fetchUserProfile();
  }, []);

  // Handle upgrade click
  const handleUpgradeClick = () => {
    setShowUpgradeDialog(true);
  };

  // Fetch expense options for FAB form
  useEffect(() => {
    const fetchExpenseOptions = async () => {
      try {
        const response = await axios.get(`${API}/expenses/options`);
        setExpenseOptions(response.data || { categories: [], payment_methods: [] });
      } catch (error) {
        console.error("Error fetching expense options:", error);
      }
    };
    fetchExpenseOptions();
  }, []);

  // Handle tab change with premium check
  const handleTabChange = (tabValue) => {
    if (!canAccessFeature(tabValue)) {
      // Still allow viewing the tab but show lock overlay
    }
    setActiveTab(tabValue);
  };

  // Render premium lock overlay for locked features
  // Special case: wealthladder shows content with blur for free users
  const renderTabContent = (tabKey, content, featureName) => {
    // Wealth Ladder is always accessible but with blurred view for free users
    if (tabKey === 'wealthladder') {
      return content;
    }
    if (!canAccessFeature(tabKey)) {
      return <PremiumLock featureName={featureName} onUpgrade={handleUpgradeClick} />;
    }
    return content;
  };

  useEffect(() => {
    // Inline fetch for budget catalogue
    const loadBudgetCatalogue = async () => {
      try {
        const token = sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/budget/catalogue`, { headers });
        setBudgetCatalogueItems(response.data || []);
      } catch (error) {
        console.error("Error fetching budget catalogue:", error);
      }
    };
    
    fetchDashboardData();
    fetchExpenses();
    fetchBudgetExpenses();
    fetchIncome();
    fetchDebt();
    fetchInvestments();
    fetchForecast();
    fetchMyNumbers();
    loadBudgetCatalogue();
    
    // Listen for My Numbers updates to refresh dependent data
    const handleMyNumbersUpdate = () => {
      console.log('My Numbers updated, refreshing dependent modules...');
      fetchForecast();
      fetchMyNumbers();
      fetchDashboardData();
    };
    
    window.addEventListener('myNumbersUpdated', handleMyNumbersUpdate);
    
    return () => {
      window.removeEventListener('myNumbersUpdated', handleMyNumbersUpdate);
    };
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      // No demo data - show empty state or error state
      setSummary(null);
    }
  };

  const fetchForecast = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/forecast`);
      setForecast(response.data);
    } catch (error) {
      console.error("Error fetching forecast:", error);
      // No demo data - show empty state
      setForecast(null);
    }
  };

  const fetchMyNumbers = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('simple_token') || sessionStorage.getItem('session_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/my-numbers`, { headers });
      setMyNumbers(response.data);
    } catch (error) {
      console.error("Error fetching my numbers:", error);
      setMyNumbers(null);
    }
  };

  const fetchExpenses = async () => {
    try {
      const response = await axios.get(`${API}/expenses`);
      setExpenses(response.data);
    } catch (error) {
      console.error("Error fetching expenses:", error);
      setExpenses([]);
    }
  };

  const fetchIncome = async () => {
    try {
      const response = await axios.get(`${API}/income`);  
      setIncome(response.data);
    } catch (error) {
      console.error("Error fetching income:", error);
      setIncome([]);
    }
  };

  const fetchDebt = async () => {
    try {
      const response = await axios.get(`${API}/debt`);
      setDebt(response.data);
    } catch (error) {
      console.error("Error fetching debt:", error);
      setDebt([]);
    }
  };

  const fetchInvestments = async () => {
    try {
      const response = await axios.get(`${API}/investments`);
      setInvestments(response.data);
    } catch (error) {
      console.error("Error fetching investments:", error);
      setInvestments([]);
    }
  };

  const fetchBudgetExpenses = async () => {
    try {
      const response = await axios.get(`${API}/budget-expenses`);
      setBudgetExpenses(response.data);
    } catch (error) {
      console.error("Error fetching budget expenses:", error);
      setBudgetExpenses([]);
    }
  };

  const addExpense = async (e) => {
    e.preventDefault();
    try {
      const expenseData = {
        ...newExpense,
        amount: parseFloat(newExpense.amount)
      };
      
      const response = await axios.post(`${API}/expenses`, expenseData);
      
      // Add the returned expense (with database ID) to local state
      setExpenses(prev => [response.data, ...prev]);
      setNewExpense({ description: "", amount: "", category: "other", date: new Date().toISOString().split('T')[0] });
      toast.success("Expense added successfully!");
      
      // Refresh dashboard data
      fetchDashboardData();
    } catch (error) {
      console.error("Error adding expense:", error);
      toast.error("Failed to add expense. Please try again.");
    }
  };

  const addIncome = async (e) => {
    e.preventDefault();
    try {
      const incomeData = {
        ...newIncome,
        amount: parseFloat(newIncome.amount)
      };
      
      const response = await axios.post(`${API}/income`, incomeData);
      
      setIncome(prev => [response.data, ...prev]);
      setNewIncome({ source: "", amount: "", date: new Date().toISOString().split('T')[0] });
      toast.success("Income added successfully!");
      
      // Refresh dashboard data
      fetchDashboardData();
    } catch (error) {
      console.error("Error adding income:", error);
      toast.error("Failed to add income. Please try again.");
    }
  };

  const addDebt = async (e) => {
    e.preventDefault();
    try {
      const debtData = {
        ...newDebt,
        amount: parseFloat(newDebt.amount),
        interest_rate: newDebt.interest_rate ? parseFloat(newDebt.interest_rate) : null
      };
      
      const response = await axios.post(`${API}/debt`, debtData);
      
      setDebt(prev => [response.data, ...prev]);
      setNewDebt({ description: "", amount: "", interest_rate: "", date: new Date().toISOString().split('T')[0] });
      toast.success("Debt added successfully!");
      
      // Refresh dashboard data
      fetchDashboardData();
    } catch (error) {
      console.error("Error adding debt:", error);
      toast.error("Failed to add debt. Please try again.");
    }
  };

  const addInvestment = async (e) => {
    e.preventDefault();
    try {
      const investmentData = {
        ...newInvestment,
        amount: parseFloat(newInvestment.amount)
      };
      
      const response = await axios.post(`${API}/investments`, investmentData);
      
      setInvestments(prev => [response.data, ...prev]);
      setNewInvestment({ description: "", amount: "", investment_type: "stocks", date: new Date().toISOString().split('T')[0] });
      toast.success("Investment added successfully!");
      
      // Refresh dashboard data
      fetchDashboardData();
    } catch (error) {
      console.error("Error adding investment:", error);
      toast.error("Failed to add investment. Please try again.");
    }
  };

  const addBudgetExpense = async (e) => {
    e.preventDefault();
    try {
      const budgetData = {
        ...newBudgetExpense,
        planned_amount: parseFloat(newBudgetExpense.planned_amount)
      };
      
      const response = await axios.post(`${API}/budget-expenses`, budgetData);
      
      setBudgetExpenses(prev => [response.data, ...prev]);
      setNewBudgetExpense({ description: "", planned_amount: "", category: "other" });
      toast.success("Budget expense added successfully!");
      
      // Refresh dashboard data
      fetchDashboardData();
    } catch (error) {
      console.error("Error adding budget expense:", error);
      toast.error("Failed to add budget expense. Please try again.");
    }
  };

  const getMaturityBadge = (level) => {
    const badges = {
      beginner: { color: "bg-orange-100 text-orange-800", icon: "📚", label: "Beginner" },
      developing: { color: "bg-blue-100 text-blue-800", icon: "🌱", label: "Developing" },
      advanced: { color: "bg-green-100 text-green-800", icon: "⭐", label: "Advanced" }
    };
    const badge = badges[level] || badges.beginner;
    return (
      <Badge className={`${badge.color} px-3 py-1`}>
        {badge.icon} {badge.label}
      </Badge>
    );
  };

  const getMaturityPercentage = (score) => {
    return Math.min(100, Math.round((score / 75) * 100));
  };

  const getProgressColor = (percentage) => {
    if (percentage < 40) return "bg-red-500";
    if (percentage < 70) return "bg-yellow-500";
    return "bg-green-500";
  };

  // Budget Module Functions
  
  // Default budget catalog items for when user hasn't defined their own
  const getDefaultBudgetItems = () => {
    return [
      // Income items
      { id: "default-income-1", name: t('quickLog.defaultItems.salary'), type: "income", category: t('quickLog.categories.primaryIncome'), defaultAmount: "" },
      { id: "default-income-2", name: t('quickLog.defaultItems.freelance'), type: "income", category: t('quickLog.categories.sideIncome'), defaultAmount: "" },
      { id: "default-income-3", name: t('quickLog.defaultItems.investments'), type: "income", category: t('quickLog.categories.passiveIncome'), defaultAmount: "" },
      { id: "default-income-4", name: t('quickLog.defaultItems.rental'), type: "income", category: t('quickLog.categories.passiveIncome'), defaultAmount: "" },
      
      // Expense items
      { id: "default-expense-1", name: t('quickLog.defaultItems.rentMortgage'), type: "expense", category: t('quickLog.categories.housing'), defaultAmount: "" },
      { id: "default-expense-2", name: t('quickLog.defaultItems.utilities'), type: "expense", category: t('quickLog.categories.housing'), defaultAmount: "" },
      { id: "default-expense-3", name: t('quickLog.defaultItems.groceries'), type: "expense", category: t('quickLog.categories.food'), defaultAmount: "" },
      { id: "default-expense-4", name: t('quickLog.defaultItems.diningOut'), type: "expense", category: t('quickLog.categories.food'), defaultAmount: "" },
      { id: "default-expense-5", name: t('quickLog.defaultItems.transportation'), type: "expense", category: t('quickLog.categories.transportation'), defaultAmount: "" },
      { id: "default-expense-6", name: t('quickLog.defaultItems.carPayment'), type: "expense", category: t('quickLog.categories.transportation'), defaultAmount: "" },
      { id: "default-expense-7", name: t('quickLog.defaultItems.healthInsurance'), type: "expense", category: t('quickLog.categories.healthcare'), defaultAmount: "" },
      { id: "default-expense-8", name: t('quickLog.defaultItems.phoneBill'), type: "expense", category: t('quickLog.categories.utilities'), defaultAmount: "" },
      { id: "default-expense-9", name: t('quickLog.defaultItems.internet'), type: "expense", category: t('quickLog.categories.utilities'), defaultAmount: "" },
      { id: "default-expense-10", name: t('quickLog.defaultItems.streaming'), type: "expense", category: t('quickLog.categories.entertainment'), defaultAmount: "" },
      { id: "default-expense-11", name: t('quickLog.defaultItems.gym'), type: "expense", category: t('quickLog.categories.health'), defaultAmount: "" },
      { id: "default-expense-12", name: t('quickLog.defaultItems.shopping'), type: "expense", category: t('quickLog.categories.personal'), defaultAmount: "" },
      { id: "default-expense-13", name: t('quickLog.defaultItems.coffee'), type: "expense", category: t('quickLog.categories.food'), defaultAmount: "" },
      { id: "default-expense-14", name: t('quickLog.defaultItems.creditCard'), type: "expense", category: t('quickLog.categories.debt'), defaultAmount: "" },
      { id: "default-expense-15", name: t('quickLog.defaultItems.studentLoan'), type: "expense", category: t('quickLog.categories.debt'), defaultAmount: "" }
    ];
  };

  // Get available budget items (user's custom items + defaults if needed)
  const getAvailableBudgetItems = () => {
    if (budgetCatalogueItems.length > 0) {
      return budgetCatalogueItems;
    }
    // If user hasn't defined budget catalog, use defaults
    return getDefaultBudgetItems();
  };

  // Fetch budget catalogue items from backend
  const fetchBudgetCatalogue = async () => {
    try {
      const response = await axios.get(`${API}/budget/catalogue`, {
        headers: getAuthHeaders()
      });
      setBudgetCatalogueItems(response.data || []);
    } catch (error) {
      console.error("Error fetching budget catalogue:", error);
    }
  };

  const addCatalogueItem = async (e) => {
    e.preventDefault();
    if (newCatalogueItem.name && newCatalogueItem.category) {
      try {
        const response = await axios.post(`${API}/budget/catalogue`, {
          name: newCatalogueItem.name,
          type: newCatalogueItem.type,
          category: newCatalogueItem.category,
          default_amount: newCatalogueItem.defaultAmount ? parseFloat(newCatalogueItem.defaultAmount) : null
        }, {
          headers: getAuthHeaders()
        });
        
        setBudgetCatalogueItems([...budgetCatalogueItems, response.data]);
        setNewCatalogueItem({ name: "", type: "expense", category: "", defaultAmount: "" });
        toast.success(t('budget.itemAdded') || 'Budget item added successfully');
      } catch (error) {
        console.error("Error adding catalogue item:", error);
        toast.error(t('budget.itemAddError') || 'Failed to add budget item');
      }
    }
  };

  const addBudgetPlanItem = (e) => {
    e.preventDefault();
    if ((newBudgetPlanItem.catalogueItemId || newBudgetPlanItem.customName) && newBudgetPlanItem.plannedAmount) {
      const item = {
        id: Date.now().toString(),
        month: currentMonth.toISOString().slice(0, 7), // YYYY-MM format
        ...newBudgetPlanItem
      };
      setMonthlyBudgetPlan([...monthlyBudgetPlan, item]);
      setNewBudgetPlanItem({ catalogueItemId: "", customName: "", plannedAmount: "", type: "expense" });
    }
  };

  // Quick Log functions removed

  // addTransaction function removed

  const editCatalogueItem = (item) => {
    setNewCatalogueItem(item);
    // Don't delete from backend yet - just remove from local state for editing
    setBudgetCatalogueItems(budgetCatalogueItems.filter(c => c.id !== item.id));
  };

  const deleteCatalogueItem = async (id) => {
    try {
      await axios.delete(`${API}/budget/catalogue/${id}`, {
        headers: getAuthHeaders()
      });
      setBudgetCatalogueItems(budgetCatalogueItems.filter(c => c.id !== id));
      toast.success(t('budget.itemDeleted') || 'Budget item deleted');
    } catch (error) {
      console.error("Error deleting catalogue item:", error);
      toast.error(t('budget.itemDeleteError') || 'Failed to delete budget item');
    }
  };

  const changeMonth = (direction) => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentMonth(newDate);
  };

  // Helper functions for budget analysis
  const getCurrentMonthBudget = () => {
    const monthKey = currentMonth.toISOString().slice(0, 7);
    return monthlyBudgetPlan.filter(item => item.month === monthKey);
  };

  // getCurrentMonthTransactions function removed

  const getPreviousMonthTransactions = () => {
    const prevMonth = new Date(currentMonth);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    const monthKey = prevMonth.toISOString().slice(0, 7);
    return actualTransactions.filter(item => item.month === monthKey);
  };

  if (!summary) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Black Header Bar - Full Width */}
      <div className="bg-black w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile Layout: Two rows */}
          <div className="flex flex-col sm:hidden py-2">
            {/* Row 1: Logo centered */}
            <div className="flex justify-center items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_prospermind-charts/artifacts/bhqmeu3t_Logo%20ProsperMind.png" 
                alt="Logo"
                className="h-32 w-auto"
              />
            </div>
            {/* Row 2: Language, Plan, Profile */}
            <div className="flex justify-center items-center gap-3 mt-1">
              <LanguageSwitcher />
              <div className="flex items-center gap-1">
                {isPremium ? (
                  <Crown className="w-4 h-4 prosper-gold" />
                ) : null}
                <span className="text-xs text-white">
                  {hasFamilySubscription ? 'Family' : (isPremium ? 'Premium' : 'Free')}
                </span>
                {!isPremium && (
                  <Button
                    onClick={handleUpgradeClick}
                    size="sm"
                    className="ml-1 text-[10px] px-2 py-0.5 rounded-full font-semibold text-black"
                    style={{ 
                      background: 'linear-gradient(135deg, #C18010 0%, #FDF298 50%, #C18010 100%)',
                    }}
                  >
                    Upgrade
                  </Button>
                )}
              </div>
              <UserProfile onLogout={onLogout} />
            </div>
          </div>
          
          {/* Desktop Layout: Single row */}
          <div className="hidden sm:flex justify-between items-center h-48">
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_prospermind-charts/artifacts/bhqmeu3t_Logo%20ProsperMind.png" 
                alt="Logo"
                className="h-48 w-auto"
              />
            </div>
            <div className="flex items-center gap-4">
              <LanguageSwitcher />
              <div className="flex items-center gap-2">
                {isPremium ? (
                  <Crown className="w-5 h-5 prosper-gold" />
                ) : null}
                <span className="text-sm text-white">
                  {hasFamilySubscription ? 'Family' : (isPremium ? 'Premium' : 'Free')} Plan
                </span>
                {hasFamilySubscription && (
                  <Button
                    onClick={() => setShowFamilyModal(true)}
                    size="sm"
                    data-testid="family-management-btn"
                    className="ml-2 text-xs px-3 py-1 rounded-full font-semibold text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <Users className="w-4 h-4 mr-1" />
                    Family
                  </Button>
                )}
                {!isPremium && (
                  <Button
                    onClick={handleUpgradeClick}
                    size="sm"
                    className="ml-2 text-xs px-3 py-1 rounded-full font-semibold text-black"
                    style={{ 
                      background: 'linear-gradient(135deg, #C18010 0%, #FDF298 50%, #C18010 100%)',
                    }}
                  >
                    Upgrade
                  </Button>
                )}
              </div>
              <UserProfile onLogout={onLogout} />
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content with Gray Background */}
      <div className="min-h-screen bg-black">

      {/* Premium Upgrade Modal */}
      <PremiumUpgradeModal
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        onSubscribe={(plan) => {
          toast.info(`${plan === 'annual' ? 'Annual' : 'Monthly'} plan selected. Payment integration coming soon!`);
          setShowUpgradeDialog(false);
        }}
      />

      {/* Family Management Modal */}
      {hasFamilySubscription && (
        <FamilyManagementModal
          isOpen={showFamilyModal}
          onClose={() => setShowFamilyModal(false)}
          token={sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken')}
        />
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-black" style={{ marginTop: 0 }}>
        {/* Quick Quiz Modal */}
        {showQuickQuiz && (
          <Dialog open={showQuickQuiz} onOpenChange={setShowQuickQuiz}>
            <DialogContent className="sm:max-w-2xl p-0 overflow-hidden">
              <QuickQuizModule 
                onComplete={handleQuizComplete}
                onNavigate={handleQuizNavigate}
              />
            </DialogContent>
          </Dialog>
        )}

        {/* Understanding Expenses Module */}
        {showUnderstandingExpenses && (
          <UnderstandingExpensesModule
            onClose={() => setShowUnderstandingExpenses(false)}
            onComplete={(result) => {
              console.log('Understanding Expenses completed:', result);
              setShowUnderstandingExpenses(false);
            }}
            isPremium={true}
          />
        )}

        {/* Start Here Button - Only show if first expense not tracked */}
        {!hasTrackedFirstExpense && (
          <div className="mb-8 text-center">
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 shadow-xl">
              <h2 className="text-2xl font-light text-white mb-2">Welcome to ProsperMind</h2>
              <p className="text-gray-400 mb-6">Begin your journey — log your first expense and start taking control</p>
              <Button
                onClick={() => {
                  setOpenExpenseFormDirectly(true);
                  setActiveTab('expenses');
                }}
                data-testid="start-here-main-btn"
                className="px-12 py-5 text-xl font-semibold bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-black rounded-full shadow-2xl transition-all duration-300 transform hover:scale-105"
              >
                <Receipt className="w-6 h-6 mr-3" />
                Start Here
              </Button>
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6 bg-transparent">
          {/* Simplified Navigation - Just Home button, modules accessed via dashboard tiles */}
          <TabsList className="flex w-full justify-start gap-2 h-auto p-2 bg-gray-900/50 rounded-lg">
            <TabsTrigger 
              value="dashboard" 
              className="text-xs md:text-sm px-3 py-2 md:px-4 md:py-2 min-w-fit whitespace-nowrap flex-shrink-0 bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 hover:scale-105 data-[state=active]:from-amber-500 data-[state=active]:to-orange-500 data-[state=active]:text-white transition-all duration-300 ease-in-out flex items-center gap-1.5"
            >
              <Home className="w-4 h-4" />
              {t('dashboardView.home')}
            </TabsTrigger>
          </TabsList>

          {/* Dashboard/Home Tab Content - V2 Premium Layout */}
          <TabsContent value="dashboard" className="space-y-0">
            <DashboardV2
              userId={user?.id || (user?.username ? `user_${user.username}` : null)}
              onNavigate={(tab, openExpenseForm = false) => {
                if (tab === 'valuecreation') {
                  setShowValueCreationModule(true);
                } else if (tab === 'scalevalue') {
                  setShowScaleValueModule(true);
                } else if (tab === 'valuecapture') {
                  setShowValueCaptureModule(true);
                } else if (tab === 'wealthLadderEducational' || tab === 'wealthladder' || tab === 'quickquiz') {
                  setShowWealthLadderModule(true);
                } else if (tab === 'understandingExpenses') {
                  setShowUnderstandingExpenses(true);
                } else if (tab === 'expenses') {
                  // If openExpenseForm is true, open the expense form directly
                  if (openExpenseForm) {
                    setOpenExpenseFormDirectly(true);
                  }
                  setActiveTab('expenses');
                } else if (tab === 'mynumbers') {
                  setActiveTab('mynumbers');
                } else if (tab === 'goals') {
                  setActiveTab('goals');
                } else if (tab === 'actionplan') {
                  setActiveTab('actionplan');
                } else if (tab === 'diagnosis') {
                  setActiveTab('diagnosis');
                } else if (tab === 'networth') {
                  setActiveTab('networth');
                } else {
                  setActiveTab(tab);
                }
              }}
              onOpenModule={(module) => {
                if (module === 'wealthLadder') {
                  setShowWealthLadderModule(true);
                } else if (module === 'understandingExpenses') {
                  setShowUnderstandingExpenses(true);
                } else if (module === 'identity') {
                  setActiveTab('identity');
                }
              }}
              canAccessFeature={canAccessFeature}
            />
            
            {/* Family Dashboard Widget - Show for family plan users */}
            {hasFamilySubscription && (
              <FamilyDashboardWidget
                token={sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken')}
                onOpenManagement={() => setShowFamilyModal(true)}
              />
            )}
          </TabsContent>

          {/* Quick Quiz Tab Content */}
          <TabsContent value="quickquiz" className="space-y-6">
            <Card className="bg-gradient-to-br from-gray-900 to-gray-800 text-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl font-bold text-white">
                  <TrendingUp className="w-6 h-6" />
                  Your Wealth Stage
                </CardTitle>
                <CardDescription className="text-gray-300">
                  Based on your quick assessment
                </CardDescription>
              </CardHeader>
              <CardContent>
                {quizResult ? (
                  <QuizResultCarousel 
                    quizResult={quizResult} 
                    onRetake={() => setShowWealthLadderModule(true)} 
                  />
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-400 mb-4">No assessment data found</p>
                    <Button
                      onClick={() => setShowWealthLadderModule(true)}
                      className="bg-yellow-500 hover:bg-yellow-600 text-black"
                    >
                      Take Assessment
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="networth" className="space-y-6">
            <NetWorthChartModule onNavigateToMyNumbers={() => setActiveTab('mynumbers')} />
          </TabsContent>

          <TabsContent value="budget" className="space-y-6">
            {renderTabContent('budget', (
            /* Budget Module with Catalogue, Monthly Planning, and Analysis */
            <Tabs value={activeBudgetTab} onValueChange={setActiveBudgetTab} className="space-y-6">
              <TabsList className="flex flex-wrap w-full justify-center md:justify-start gap-2 h-auto p-2 bg-muted/50 rounded-lg">
                <TabsTrigger value="catalogue" className="text-xs md:text-sm px-3 py-2 min-w-fit whitespace-nowrap flex-shrink-0 text-black hover:bg-gray-200 hover:scale-105 transition-all duration-300 ease-in-out">{t('budget.budgetCatalogue')}</TabsTrigger>
                <TabsTrigger value="monthly" className="text-xs md:text-sm px-3 py-2 min-w-fit whitespace-nowrap flex-shrink-0 text-black hover:bg-gray-200 hover:scale-105 transition-all duration-300 ease-in-out">{t('budget.monthlyPlanning')}</TabsTrigger>
                <TabsTrigger value="analysis" className="text-xs md:text-sm px-3 py-2 min-w-fit whitespace-nowrap flex-shrink-0 text-black hover:bg-gray-200 hover:scale-105 transition-all duration-300 ease-in-out">{t('budget.budgetAnalysis')}</TabsTrigger>
              </TabsList>

              {/* Budget Catalogue Tab - Master Template */}
              <TabsContent value="catalogue" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-black text-2xl font-bold">
                      <PieChart className="w-5 h-5" />
                      {t('budget.budgetCatalogue')}
                    </CardTitle>
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <CardDescription className="flex-1">{t('budget.budgetCatalogueDesc')}</CardDescription>
                      <CurrencySelector />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {/* Add Catalogue Item Form */}
                      <Card className="border-2 border-dashed border-gray-300">
                        <CardHeader>
                          <CardTitle className="text-base">{t('budget.addNewItem')}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <form onSubmit={addCatalogueItem} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div>
                                <Label htmlFor="catalogue-type">{t('budget.type')}</Label>
                                <Select 
                                  value={newCatalogueItem.type} 
                                  onValueChange={(value) => setNewCatalogueItem(prev => ({ ...prev, type: value }))}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder={t('budget.type')} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="income">{t('budgetModule.income')}</SelectItem>
                                    <SelectItem value="expense">{t('budgetModule.expense')}</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label htmlFor="catalogue-category">{t('budget.category')}</Label>
                                <Select 
                                  value={newCatalogueItem.category} 
                                  onValueChange={(value) => setNewCatalogueItem(prev => ({ ...prev, category: value }))}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder={t('budget.category')} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {newCatalogueItem.type === 'income' ? (
                                      <>
                                        <SelectItem value="salary">{t('quickLog.defaultItems.salary')}</SelectItem>
                                        <SelectItem value="freelance">{t('quickLog.defaultItems.freelance')}</SelectItem>
                                        <SelectItem value="investment">{t('quickLog.defaultItems.investments')}</SelectItem>
                                        <SelectItem value="business">{t('quickLog.categories.sideIncome')}</SelectItem>
                                        <SelectItem value="other">{t('networth.other')}</SelectItem>
                                      </>
                                    ) : (
                                      <>
                                        <SelectItem value="housing">{t('quickLog.categories.housing')}</SelectItem>
                                        <SelectItem value="food">{t('quickLog.categories.food')}</SelectItem>
                                        <SelectItem value="transportation">{t('quickLog.categories.transportation')}</SelectItem>
                                        <SelectItem value="utilities">{t('quickLog.categories.utilities')}</SelectItem>
                                        <SelectItem value="healthcare">{t('quickLog.categories.healthcare')}</SelectItem>
                                        <SelectItem value="entertainment">{t('quickLog.categories.entertainment')}</SelectItem>
                                        <SelectItem value="shopping">{t('quickLog.defaultItems.shopping')}</SelectItem>
                                        <SelectItem value="education">{t('expenseTracker.categories.education')}</SelectItem>
                                        <SelectItem value="other">{t('networth.other')}</SelectItem>
                                      </>
                                    )}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label htmlFor="catalogue-name">{t('budget.itemName')}</Label>
                                <Input
                                  id="catalogue-name"
                                  placeholder={t('budget.itemName')}
                                  value={newCatalogueItem.name}
                                  onChange={(e) => setNewCatalogueItem(prev => ({ ...prev, name: e.target.value }))}
                                  required
                                />
                              </div>
                              <div>
                                <Label htmlFor="catalogue-default">{t('budget.defaultAmount')}</Label>
                                <Input
                                  id="catalogue-default"
                                  type="number"
                                  step="1"
                                  placeholder="0"
                                  value={newCatalogueItem.defaultAmount}
                                  onChange={(e) => setNewCatalogueItem(prev => ({ ...prev, defaultAmount: e.target.value }))}
                                />
                              </div>
                            </div>
                            <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                              {t('budget.addToCatalogue')}
                            </Button>
                          </form>
                        </CardContent>
                      </Card>

                      {/* Catalogue Items Display */}
                      {budgetCatalogueItems.length > 0 && (
                        <div className="space-y-4">
                          <h3 className="text-lg font-semibold">{t('budget.yourBudgetCatalogue')}</h3>
                          
                          {/* Income Items */}
                          {budgetCatalogueItems.filter(item => item.type === 'income').length > 0 && (
                            <Card className="border-green-200">
                              <CardHeader>
                                <CardTitle className="text-green-700 flex items-center gap-2">
                                  <TrendingUp className="w-5 h-5" />
                                  💰 {t('budget.incomeItems')}
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-2">
                                  {budgetCatalogueItems.filter(item => item.type === 'income').map((item) => (
                                    <div key={item.id} className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                                      <div>
                                        <div className="font-medium">{item.name}</div>
                                        <div className="text-sm text-slate-600 capitalize">{item.category}</div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        {item.defaultAmount && (
                                          <div className="text-green-600 font-semibold">
                                            {formatCurrency(item.defaultAmount)}
                                          </div>
                                        )}
                                        <div className="flex gap-2">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => editCatalogueItem(item)}
                                            className="text-blue-600 hover:text-blue-700"
                                          >
                                            ✏️
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => deleteCatalogueItem(item.id)}
                                            className="text-red-600 hover:text-red-700"
                                          >
                                            🗑️
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </CardContent>
                            </Card>
                          )}

                          {/* Expense Items */}
                          {budgetCatalogueItems.filter(item => item.type === 'expense').length > 0 && (
                            <Card className="border-red-200">
                              <CardHeader>
                                <CardTitle className="text-red-700 flex items-center gap-2">
                                  <TrendingDown className="w-5 h-5" />
                                  💸 {t('budget.expenseItems')}
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-2">
                                  {budgetCatalogueItems.filter(item => item.type === 'expense').map((item) => (
                                    <div key={item.id} className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                                      <div>
                                        <div className="font-medium">{item.name}</div>
                                        <div className="text-sm text-slate-600 capitalize">{item.category}</div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        {item.defaultAmount && (
                                          <div className="text-red-600 font-semibold">
                                            {formatCurrency(item.defaultAmount)}
                                          </div>
                                        )}
                                        <div className="flex gap-2">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => editCatalogueItem(item)}
                                            className="text-blue-600 hover:text-blue-700"
                                          >
                                            ✏️
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => deleteCatalogueItem(item.id)}
                                            className="text-red-600 hover:text-red-700"
                                          >
                                            🗑️
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </CardContent>
                            </Card>
                          )}
                        </div>
                      )}

                      {/* Empty State */}
                      {budgetCatalogueItems.length === 0 && (
                        <Card className="border-2 border-dashed border-gray-300">
                          <CardContent className="text-center py-12">
                            <PieChart className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No Items in Catalogue</h3>
                            <p className="text-gray-500 mb-4">
                              Start by adding income and expense items to your budget catalogue. These will serve as templates for your monthly planning.
                            </p>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Monthly Budget Tab */}
              <TabsContent value="monthly" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-black text-2xl font-bold">
                      <Calendar className="w-5 h-5" />
                      📅 {t('budget.monthlyPlanning')}
                    </CardTitle>
                    <CardDescription>
                      {(() => {
                        const translation = t('budget.monthlyPlanningDesc');
                        return translation === 'Description' ? 'Create your monthly budget plan using catalogue items and custom entries' : translation;
                      })()}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {/* Month Selector */}
                      <div className="flex justify-between items-center p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                        <div>
                          <h3 className="font-semibold text-indigo-700">{t('budgetModule.budgetPlanningFor')}</h3>
                          <p className="text-sm text-indigo-600">
                            {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => changeMonth(-1)}>
                            {t('budgetModule.previous')}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setCurrentMonth(new Date())}>
                            {t('budgetModule.current')}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => changeMonth(1)}>
                            {t('budgetModule.next')}
                          </Button>
                        </div>
                      </div>

                      {/* Add Budget Plan Item */}
                      <Card className="border-2 border-dashed border-gray-300">
                        <CardHeader>
                          <CardTitle className="text-base flex items-center gap-2">
                            <Plus className="w-4 h-4" />
                            {t('budgetModule.addBudgetPlanItem')}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <form onSubmit={addBudgetPlanItem} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div>
                                <Label htmlFor="plan-type">{t('budgetModule.type')}</Label>
                                <Select 
                                  value={newBudgetPlanItem.type} 
                                  onValueChange={(value) => setNewBudgetPlanItem(prev => ({ ...prev, type: value, catalogueItemId: '' }))}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder={t('budgetModule.type')} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="income">{t('budgetModule.income')}</SelectItem>
                                    <SelectItem value="expense">{t('budgetModule.expense')}</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label htmlFor="plan-catalogue">{t('budgetModule.fromCatalogue')}</Label>
                                <Select 
                                  value={newBudgetPlanItem.catalogueItemId} 
                                  onValueChange={(value) => {
                                    const selectedItem = budgetCatalogueItems.find(item => item.id === value);
                                    setNewBudgetPlanItem(prev => ({ 
                                      ...prev, 
                                      catalogueItemId: value,
                                      plannedAmount: selectedItem?.defaultAmount || prev.plannedAmount
                                    }));
                                  }}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select item" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {budgetCatalogueItems
                                      .filter(item => item.type === newBudgetPlanItem.type)
                                      .map(item => (
                                        <SelectItem key={item.id} value={item.id}>
                                          {item.name} ({item.category})
                                        </SelectItem>
                                      ))
                                    }
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label htmlFor="plan-custom">Custom Name (if not from catalogue)</Label>
                                <Input
                                  id="plan-custom"
                                  placeholder="Custom item name"
                                  value={newBudgetPlanItem.customName}
                                  onChange={(e) => setNewBudgetPlanItem(prev => ({ ...prev, customName: e.target.value }))}
                                  disabled={!!newBudgetPlanItem.catalogueItemId}
                                />
                              </div>
                              <div>
                                <Label htmlFor="plan-amount">Planned Amount</Label>
                                <Input
                                  id="plan-amount"
                                  type="number"
                                  step="1"
                                  placeholder="0"
                                  value={newBudgetPlanItem.plannedAmount}
                                  onChange={(e) => setNewBudgetPlanItem(prev => ({ ...prev, plannedAmount: e.target.value }))}
                                  required
                                />
                              </div>
                            </div>
                            <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                              Add to Monthly Plan
                            </Button>
                          </form>
                        </CardContent>
                      </Card>

                      {/* Current Month Budget Plan */}
                      {getCurrentMonthBudget().length > 0 && (
                        <div className="space-y-4">
                          <h3 className="text-lg font-semibold">Monthly Budget Plan</h3>
                          
                          {/* Income Plan */}
                          {getCurrentMonthBudget().filter(item => item.type === 'income').length > 0 && (
                            <Card className="border-green-200">
                              <CardHeader>
                                <CardTitle className="text-green-700 flex items-center gap-2">
                                  <TrendingUp className="w-5 h-5" />
                                  💰 Planned Income
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-2">
                                  {getCurrentMonthBudget().filter(item => item.type === 'income').map((item) => {
                                    const catalogueItem = budgetCatalogueItems.find(c => c.id === item.catalogueItemId);
                                    return (
                                      <div key={item.id} className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                                        <div>
                                          <div className="font-medium">
                                            {catalogueItem ? catalogueItem.name : item.customName}
                                          </div>
                                          {catalogueItem && (
                                            <div className="text-sm text-slate-600 capitalize">{catalogueItem.category}</div>
                                          )}
                                        </div>
                                        <div className="text-green-600 font-semibold">
                                          +{formatCurrency(item.plannedAmount)}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </CardContent>
                            </Card>
                          )}

                          {/* Expense Plan */}
                          {getCurrentMonthBudget().filter(item => item.type === 'expense').length > 0 && (
                            <Card className="border-red-200">
                              <CardHeader>
                                <CardTitle className="text-red-700 flex items-center gap-2">
                                  <TrendingDown className="w-5 h-5" />
                                  💸 Planned Expenses
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-2">
                                  {getCurrentMonthBudget().filter(item => item.type === 'expense').map((item) => {
                                    const catalogueItem = budgetCatalogueItems.find(c => c.id === item.catalogueItemId);
                                    return (
                                      <div key={item.id} className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                                        <div>
                                          <div className="font-medium">
                                            {catalogueItem ? catalogueItem.name : item.customName}
                                          </div>
                                          {catalogueItem && (
                                            <div className="text-sm text-slate-600 capitalize">{catalogueItem.category}</div>
                                          )}
                                        </div>
                                        <div className="text-red-600 font-semibold">
                                          -{formatCurrency(item.plannedAmount)}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </CardContent>
                            </Card>
                          )}

                          {/* Monthly Plan Summary */}
                          <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
                            <CardHeader>
                              <CardTitle className="text-indigo-700 flex items-center gap-2">
                                <Calculator className="w-5 h-5" />
                                📊 Monthly Plan Summary
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="text-center p-4 bg-green-100 rounded-lg">
                                  <div className="text-xl font-bold text-green-700">
                                    {formatCurrency(
                                      getCurrentMonthBudget()
                                        .filter(item => item.type === 'income')
                                        .reduce((sum, item) => sum + parseFloat(item.plannedAmount || 0), 0)
                                    )}
                                  </div>
                                  <div className="text-sm text-green-600">Planned Income</div>
                                </div>
                                <div className="text-center p-4 bg-red-100 rounded-lg">
                                  <div className="text-xl font-bold text-red-700">
                                    {formatCurrency(
                                      getCurrentMonthBudget()
                                        .filter(item => item.type === 'expense')
                                        .reduce((sum, item) => sum + parseFloat(item.plannedAmount || 0), 0)
                                    )}
                                  </div>
                                  <div className="text-sm text-red-600">Planned Expenses</div>
                                </div>
                                <div className="text-center p-4 bg-blue-100 rounded-lg">
                                  <div className={`text-xl font-bold ${
                                    (getCurrentMonthBudget().filter(item => item.type === 'income').reduce((sum, item) => sum + parseFloat(item.plannedAmount || 0), 0) - 
                                     getCurrentMonthBudget().filter(item => item.type === 'expense').reduce((sum, item) => sum + parseFloat(item.plannedAmount || 0), 0)) >= 0 
                                    ? 'text-green-700' : 'text-red-700'
                                  }`}>
                                    {formatCurrency(
                                      getCurrentMonthBudget().filter(item => item.type === 'income').reduce((sum, item) => sum + parseFloat(item.plannedAmount || 0), 0) - 
                                      getCurrentMonthBudget().filter(item => item.type === 'expense').reduce((sum, item) => sum + parseFloat(item.plannedAmount || 0), 0)
                                    )}
                                  </div>
                                  <div className="text-sm text-blue-600">Net Planned Budget</div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      )}

                      {/* Empty State */}
                      {getCurrentMonthBudget().length === 0 && (
                        <Card className="border-2 border-dashed border-gray-300">
                          <CardContent className="text-center py-12">
                            <Calendar className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No Budget Plan for This Month</h3>
                            <p className="text-gray-500 mb-4">
                              {budgetCatalogueItems.length > 0 
                                ? "Select items from your catalogue or add custom items to create your monthly budget plan."
                                : "Create items in the Budget Catalogue first, then come back to plan your monthly budget."
                              }
                            </p>
                            {budgetCatalogueItems.length === 0 && (
                              <Button 
                                onClick={() => setActiveBudgetTab('catalogue')}
                                className="bg-indigo-600 hover:bg-indigo-700"
                              >
                                Go to Budget Catalogue
                              </Button>
                            )}
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Budget Analysis Tab */}
              <TabsContent value="analysis" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-black text-2xl font-bold">
                      <BarChart3 className="w-5 h-5" />
                      📊 {t('budget.budgetAnalysis')}
                    </CardTitle>
                    <CardDescription>
                      {(() => {
                        const translation = t('budget.budgetAnalysisDesc');
                        return translation === 'Description' ? 'Compare your plan vs actual results vs previous month performance' : translation;
                      })()}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {/* Month Selector */}
                      <div className="flex justify-between items-center p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <div>
                          <h3 className="font-semibold text-purple-700">{t('budgetModule.analysisFor')}</h3>
                          <p className="text-sm text-purple-600">
                            {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => changeMonth(-1)}>
                            {t('budgetModule.previous')}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setCurrentMonth(new Date())}>
                            {t('budgetModule.current')}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => changeMonth(1)}>
                            {t('budgetModule.next')}
                          </Button>
                        </div>
                      </div>

                      {/* Budget Planning Focus - Analysis removed with Quick Log */}
                      {getCurrentMonthBudget().length === 0 && (
                        <Card className="border-2 border-dashed border-gray-300">
                          <CardContent className="text-center py-12">
                            <BarChart3 className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('budgetModule.readyToPlanBudget')}</h3>
                            <p className="text-gray-500 mb-4">
                              {t('budgetModule.createBudgetPlanMessage')}
                            </p>
                            <Button 
                              onClick={() => setActiveBudgetTab('monthly')}
                              variant="outline"
                              size="sm"
                            >
                              {t('budgetModule.createBudgetPlan')}
                            </Button>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
            ), 'Budget Planning')}
          </TabsContent>

          <TabsContent value="expenses" className="space-y-6">
            <ExpenseTrackerModule initialShowAddForm={openExpenseFormDirectly} />
          </TabsContent>

          {/* Analytics Module - Standalone */}
          <TabsContent value="analytics" className="space-y-6">
            {renderTabContent('analytics', <AnalyticsModule />, 'Analytics')}
          </TabsContent>

          {/* Savings Potential Module */}
          <TabsContent value="savingspotential" className="space-y-6">
            <SavingsPotentialModule onNavigateToMyNumbers={() => setActiveTab('mynumbers')} />
          </TabsContent>

          <TabsContent value="actionplan" className="space-y-6">
            {renderTabContent('actionplan', <ActionPlanModule />, 'Action Plan')}
          </TabsContent>

          <TabsContent value="goals" className="space-y-6">
            {renderTabContent('goals', <GoalsModule />, 'Goals & Objectives')}
          </TabsContent>

          <TabsContent value="journal" className="space-y-6">
            {renderTabContent('journal', <JournalModule />, 'Financial Journal')}
          </TabsContent>

          <TabsContent value="beliefs" className="space-y-6">
            {renderTabContent('beliefs', <BeliefsModule />, 'Beliefs Assessment')}
          </TabsContent>

          <TabsContent value="habits" className="space-y-6">
            {renderTabContent('habits', <HabitsModule />, 'Habits Tracking')}
          </TabsContent>

          <TabsContent value="identity" className="space-y-6">
            <IdentityModule onClose={() => setActiveTab('dashboard')} />
          </TabsContent>

          {/* Vision Module */}
          <TabsContent value="vision" className="space-y-6">
            {renderTabContent('vision', <VisionModule />, 'Vision')}
          </TabsContent>

          <TabsContent value="diagnosis" className="space-y-6">
            {/* Financial Maturity Assessment - Diagnosis section */}
            <Card className="bg-white shadow-sm border border-gray-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl font-bold text-black">
                  <BarChart3 className="w-5 h-5" />
                  {t('diagnosis.financialMaturityAssessment')}
                </CardTitle>
                <CardDescription>{t('diagnosis.assessmentDescription')}</CardDescription>
              </CardHeader>
              <CardContent>
                {summary?.diagnosis_score ? (
                  <div className="space-y-6">
                    <div className="text-center">
                      <div className="text-4xl font-bold text-emerald-600 mb-2">
                        {summary.diagnosis_score}/100
                      </div>
                      <p className="text-slate-600">{t('diagnosis.yourFinancialMaturityScore')}</p>
                    </div>
                    
                    <GradientProgressBar progress={summary.diagnosis_score} />
                    
                    {/* Take Test Button */}
                    <div className="mt-4 text-center">
                      <Button 
                        onClick={() => setNeedsDiagnosis(true)}
                        variant="outline"
                        size="sm"
                        className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200"
                      >
                        {t('diagnosis.retakeAssessment')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-slate-600 mb-4">{t('diagnosis.completeAssessment')}</p>
                    <Button onClick={() => setNeedsDiagnosis(true)} className="bg-emerald-600 hover:bg-emerald-700">
                      {t('diagnosis.takeAssessment')}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* My Numbers Module - Standalone */}
          <TabsContent value="mynumbers" className="space-y-6">
            {renderTabContent('mynumbers', <MyNumbersModule />, 'My Numbers')}
          </TabsContent>

          {/* Freedom KPIs Module - Read-only analytics */}
          <TabsContent value="freedomkpis" className="space-y-6">
            {renderTabContent('freedomkpis', (
              <FreedomKPIsModule 
                myNumbersData={myNumbers}
                forecastData={{
                  incomeGrowth: 5,
                  expensesGrowth: 5,
                  investmentReturn: 4,
                  passiveGrowth: 4,
                  inflation: 3
                }}
              />
            ), 'Freedom KPIs')}
          </TabsContent>

          <TabsContent value="forecast" className="space-y-6">
            {renderTabContent('forecast', (
            <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl font-bold text-white">
                  <TrendingUp className="w-5 h-5 text-blue-400" />
                  {t('forecast.title')}
                </CardTitle>
                <CardDescription className="text-gray-400">
                  {(() => {
                    const translation = t('forecast.description');
                    const source = forecast?.data_source || 'none';
                    const sourceLabel = source === 'combined' ? 'My Numbers + Net Worth' : 
                                       source === 'my-numbers' ? 'My Numbers' :
                                       source === 'net-worth' ? 'Net Worth' :
                                       source === 'expenses' ? 'Expense History' : 'No data';
                    return `12-month financial projection • Data source: ${sourceLabel}`;
                  })()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {forecast && (forecast.monthly_income > 0 || forecast.net_worth !== 0) ? (
                  <div className="space-y-6">
                    <ForecastChart data={forecast} />
                    
                    {/* Key Financial Metrics */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-emerald-900/30 rounded-lg border border-emerald-700/50">
                        <div className="text-2xl font-bold text-emerald-400">
                          {formatCurrency(forecast.monthly_income || 0)}
                        </div>
                        <div className="text-xs text-emerald-400">Monthly Income</div>
                      </div>
                      <div className="text-center p-4 bg-red-900/30 rounded-lg border border-red-700/50">
                        <div className="text-2xl font-bold text-red-400">
                          {formatCurrency(forecast.monthly_expenses || 0)}
                        </div>
                        <div className="text-xs text-red-400">Monthly Expenses</div>
                      </div>
                      <div className="text-center p-4 bg-blue-900/30 rounded-lg border border-blue-700/50">
                        <div className="text-2xl font-bold text-blue-400">
                          {formatCurrency(forecast.monthly_savings || 0)}
                        </div>
                        <div className="text-xs text-blue-400">Monthly Savings</div>
                      </div>
                      <div className="text-center p-4 bg-purple-900/30 rounded-lg border border-purple-700/50">
                        <div className="text-2xl font-bold text-purple-400">
                          {forecast.savings_rate || 0}%
                        </div>
                        <div className="text-xs text-purple-400">Savings Rate</div>
                      </div>
                    </div>

                    {/* Net Worth & Projections */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 bg-gradient-to-br from-slate-700 to-slate-800 rounded-lg text-white border border-slate-600">
                        <div className="text-sm opacity-80 mb-1">Net Worth</div>
                        <div className="text-2xl font-bold">
                          {formatCurrency(forecast.net_worth || 0)}
                        </div>
                        <div className="text-xs opacity-60 mt-2">
                          Assets: {formatCurrency(forecast.total_assets || 0)} • Debt: {formatCurrency(forecast.total_liabilities || 0)}
                        </div>
                      </div>
                      <div className="p-4 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-lg text-white border border-emerald-500">
                        <div className="text-sm opacity-80 mb-1">Projected Annual Savings</div>
                        <div className="text-2xl font-bold">
                          {formatCurrency(forecast.projected_savings || 0)}
                        </div>
                        <div className="text-xs opacity-60 mt-2">
                          {formatCurrency((forecast.projected_savings || 0) / 12)}/month
                        </div>
                      </div>
                      <div className="p-4 bg-gradient-to-br from-amber-600 to-amber-700 rounded-lg text-white border border-amber-500">
                        <div className="text-sm opacity-80 mb-1">Emergency Fund</div>
                        <div className="text-2xl font-bold">
                          {forecast.emergency_fund_months || 0} months
                        </div>
                        <div className="text-xs opacity-60 mt-2">
                          {forecast.emergency_fund_months >= 6 ? '✓ On track' : forecast.emergency_fund_months >= 3 ? '⚠ Building' : '⚠ Needs attention'}
                        </div>
                      </div>
                    </div>

                    {/* Income Breakdown */}
                    {(forecast.passive_income > 0 || forecast.debt_payments > 0) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {forecast.passive_income > 0 && (
                          <div className="flex items-center justify-between p-3 bg-green-900/30 rounded-lg border border-green-700/50">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-green-500"></div>
                              <span className="text-sm text-green-400">Passive Income</span>
                            </div>
                            <span className="font-semibold text-green-400">{formatCurrency(forecast.passive_income)}/mo</span>
                          </div>
                        )}
                        {forecast.debt_payments > 0 && (
                          <div className="flex items-center justify-between p-3 bg-orange-900/30 rounded-lg border border-orange-700/50">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                              <span className="text-sm text-orange-400">Debt Payments</span>
                            </div>
                            <span className="font-semibold text-orange-400">{formatCurrency(forecast.debt_payments)}/mo</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <Button 
                      onClick={() => setShowForecast(!showForecast)}
                      variant="outline" 
                      size="sm"
                      className="w-full border-gray-600 text-gray-300 hover:bg-gray-700"
                    >
                      {showForecast ? 'Hide' : 'Show'} Detailed Forecast
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-800 flex items-center justify-center">
                      <TrendingUp className="w-8 h-8 text-gray-500" />
                    </div>
                    <p className="text-gray-300 mb-2 font-medium">No financial data available</p>
                    <p className="text-gray-500 text-sm mb-4">Add your financial information in "My Numbers" or "Net Worth" to generate forecasts</p>
                    <div className="flex gap-2 justify-center">
                      <Button onClick={() => { setActiveTab('diagnosis'); }} variant="outline" size="sm" className="border-gray-600 text-gray-300 hover:bg-gray-700">
                        Add My Numbers
                      </Button>
                      <Button onClick={() => setActiveTab('networth')} variant="outline" size="sm" className="border-gray-600 text-gray-300 hover:bg-gray-700">
                        Add Net Worth
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            ), 'Cash Flow Forecast')}
          </TabsContent>

          <TabsContent value="learning" className="space-y-6">
            {renderTabContent('learning', (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl font-bold text-black">
                  <BookOpen className="w-5 h-5" />
                  {t('learning.title')}
                </CardTitle>
                <CardDescription>
                  {(() => {
                    const translation = t('learning.description');
                    return translation === 'Description' ? 'Expand your financial knowledge with our curated resources' : translation;
                  })()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Interactive Lessons - Featured */}
                <div className="mb-6">
                  <h3 className="font-semibold text-slate-700 mb-4">
                    {i18n.language === 'es' ? '🎓 Lecciones Interactivas' : '🎓 Interactive Lessons'}
                  </h3>
                  <button
                    onClick={() => setShowValueCreationModule(true)}
                    className="w-full p-5 bg-gradient-to-r from-yellow-50 via-amber-50 to-orange-50 hover:from-yellow-100 hover:via-amber-100 hover:to-orange-100 border-2 border-yellow-300 hover:border-yellow-400 rounded-xl transition-all group text-left"
                    data-testid="value-creation-lesson-btn"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-0.5 bg-yellow-500 text-white text-xs font-bold rounded">
                            {i18n.language === 'es' ? 'NUEVO' : 'NEW'}
                          </span>
                          <span className="text-gray-500 text-sm">5-7 min</span>
                        </div>
                        <h4 className="font-bold text-lg text-gray-900 group-hover:text-yellow-700 transition-colors mb-1">
                          {i18n.language === 'es' ? 'Creación de Valor' : 'Value Creation'}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {i18n.language === 'es' 
                            ? 'Descubre las tres fuerzas que determinan tu potencial de ingresos y cuál podría estar frenándote.'
                            : 'Discover the three forces that determine your earning potential — and which one might be holding you back.'}
                        </p>
                      </div>
                      <div className="ml-4 w-12 h-12 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                        <Lightbulb className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Featured Articles */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-slate-700">📚 Featured Articles</h3>
                    <div className="space-y-3">
                      <div className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors">
                        <h4 className="font-medium mb-2">Building Your Emergency Fund</h4>
                        <p className="text-sm text-slate-600">Learn why emergency funds are crucial and how to build one effectively.</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors">
                        <h4 className="font-medium mb-2">Investment Basics for Beginners</h4>
                        <p className="text-sm text-slate-600">Start your investment journey with these fundamental concepts.</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors">
                        <h4 className="font-medium mb-2">Debt Management Strategies</h4>
                        <p className="text-sm text-slate-600">Effective approaches to paying down and managing debt.</p>
                      </div>
                    </div>
                  </div>

                  {/* Quick Tips */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-slate-700">💡 Quick Tips</h3>
                    <div className="space-y-3">
                      <div className="p-4 bg-gradient-to-r from-blue-50 to-emerald-50 rounded-lg border border-blue-200">
                        <h4 className="font-medium mb-2">💰 Save on Groceries</h4>
                        <p className="text-sm text-slate-600">Plan meals, use coupons, and buy generic brands to reduce grocery costs by 20-30%.</p>
                      </div>
                      <div className="p-4 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg border border-emerald-200">
                        <h4 className="font-medium mb-2">📱 Automate Savings</h4>
                        <p className="text-sm text-slate-600">Set up automatic transfers to savings accounts to build wealth consistently.</p>
                      </div>
                      <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                        <h4 className="font-medium mb-2">🎯 Track Net Worth</h4>
                        <p className="text-sm text-slate-600">Monitor your net worth monthly to gauge progress toward financial goals.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Daily Tip */}
                <Card className="bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      💡 Daily Financial Tip
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm mb-3">
                      <strong>The 50/30/20 Rule:</strong> Allocate 50% of your income to needs, 30% to wants, and 20% to savings and debt repayment. This simple framework helps maintain balanced spending habits.
                    </p>
                    <Button size="sm" variant="outline">
                      Learn More
                    </Button>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
            ), 'Learning Resources')}
          </TabsContent>

          <TabsContent value="wealthladder" className="space-y-6">
            {renderTabContent('wealthladder', <WealthLadderModule quizResult={quizResult} onRetakeQuiz={() => setShowWealthLadderModule(true)} />, 'Wealth Ladder')}
          </TabsContent>

          <TabsContent value="moneycycle" className="space-y-6">
            {renderTabContent('moneycycle', <MoneyCycleModule onNavigate={(tab) => setActiveTab(tab)} />, 'Money Cycle')}
          </TabsContent>
          
          {/* Animated Money Cycle */}
          <TabsContent value="moneycycle-animated" className="space-y-6">
            {renderTabContent('moneycycle', <AnimatedMoneyCycleModule onNavigate={(tab) => setActiveTab(tab)} />, 'Money Cycle (Animated)')}
          </TabsContent>
        </Tabs>
      </main>

      {/* Value Creation Module */}
      {showValueCreationModule && (
        <ValueCreationModuleV2 
          onClose={() => setShowValueCreationModule(false)}
          userProfile={userProfile}
          isPremium={isPremium}
        />
      )}

      {/* Scale Value Module */}
      {showScaleValueModule && (
        <ScaleValueModule 
          onClose={() => setShowScaleValueModule(false)}
          onSavePlan={async (plan) => {
            try {
              await axios.post(`${API}/scaling-plan/save`, plan);
            } catch (error) {
              console.error('Error saving scaling plan:', error);
            }
          }}
        />
      )}

      {/* Value Capture Module */}
      {showValueCaptureModule && (
        <ValueCaptureModule 
          onClose={() => setShowValueCaptureModule(false)}
          onSavePlan={async (plan) => {
            try {
              await axios.post(`${API}/value-capture-plan/save`, plan);
            } catch (error) {
              console.error('Error saving value capture plan:', error);
            }
          }}
        />
      )}

      {/* Wealth Ladder Educational Module */}
      {showWealthLadderModule && (
        <WealthLadderEducationalModule
          onComplete={handleWealthLadderComplete}
          onClose={() => setShowWealthLadderModule(false)}
          existingResult={quizResult}
        />
      )}
    </div>
    </>
  );
};

const CustomLogin = () => {
  const handleGoogleLogin = () => {
    // Redirect to Auth0 login page
    window.location.href = '/login';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 to-slate-900 p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          {/* Logo - Enlarged */}
          <a href="/" className="hover:opacity-80 transition-opacity duration-300">
            <h1 className="text-6xl font-bold bg-gradient-to-r from-yellow-400 via-orange-500 to-red-600 bg-clip-text text-transparent mb-2">
              ProsperMind
            </h1>
          </a>
          <p className="text-gray-400 text-lg">
            Your Financial Health Companion
          </p>
        </div>

        <Card className="bg-white/95 backdrop-blur-sm shadow-2xl border-0">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Welcome Back
              </h2>
              <p className="text-gray-600 text-lg">
                Your path to financial freedom starts here
              </p>
            </div>

            {/* Google Login Button - Matching Homepage Style */}
            <button
              onClick={handleGoogleLogin}
              className="w-full text-black px-6 py-4 rounded-full font-bold hover:opacity-95 transition-all duration-300 shadow-xl relative overflow-hidden group mb-6"
              style={{ 
                background: 'linear-gradient(135deg, #C18010 0%, #AA7D22 15%, #FDF298 35%, #C18010 50%, #8B4213 65%, #C18010 80%, #FDF298 95%, #AA7D22 100%)',
                boxShadow: '0 6px 20px rgba(193, 128, 16, 0.5), inset 0 1px 0 rgba(253, 242, 152, 0.8)'
              }}
            >
              <div className="flex items-center justify-center space-x-3">
                <svg className="w-6 h-6" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Continue with Google</span>
              </div>
            </button>

            <div className="text-center">
              <p className="text-sm text-gray-500">
                By continuing, you agree to our Terms of Service and Privacy Policy
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const Login = () => {
  // Use custom login as default
  return <CustomLogin />;
};
const ProfileHandler = () => {
  // Redirect to dashboard since we don't have a separate profile page yet
  useEffect(() => {
    window.location.href = '/dashboard';
  }, []);

  return (
    <div className="flex items-center justify-center min-h-screen bg-black">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
        <p className="text-slate-300">Redirecting to dashboard...</p>
      </div>
    </div>
  );
};

// Join Household Page Wrapper to extract URL params
const JoinHouseholdPageWrapper = ({ isAuthenticated, token }) => {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  
  const handleLoginRequired = () => {
    // Store invite code in session storage and redirect to login
    sessionStorage.setItem('pendingInviteCode', inviteCode);
    navigate('/login');
  };
  
  const handleJoinSuccess = () => {
    // Redirect to dashboard after successful join
    navigate('/dashboard');
  };
  
  return (
    <JoinHouseholdPage
      inviteCode={inviteCode}
      token={token}
      isAuthenticated={isAuthenticated}
      onLoginRequired={handleLoginRequired}
      onJoinSuccess={handleJoinSuccess}
    />
  );
};

const App = () => {
  const { user, isAuthenticated, isLoading, logout, getAccessToken } = useAuth();
  const { t } = useTranslation();
  const { currentLanguage, reloadUserLanguage } = useLanguage();
  const [needsDiagnosis, setNeedsDiagnosis] = useState(false);

  // Set up auth token for API calls when authenticated
  useEffect(() => {
    const setupAuth = async () => {
      if (isAuthenticated) {
        try {
          const token = getAccessToken();
          sessionStorage.setItem('authToken', token);
        } catch (error) {
          console.error('Error getting access token:', error);
        }
      } else {
        // Clear auth token when not authenticated
        sessionStorage.removeItem('authToken');
        delete axios.defaults.headers.common['Authorization'];
      }
    };
    setupAuth();
  }, [isAuthenticated, getAccessToken]);

  // Force language reload when user is authenticated
  useEffect(() => {
    const checkAndLoadUserLanguage = async () => {
      if (isAuthenticated && reloadUserLanguage) {
        // Add a small delay to ensure the auth token is set
        setTimeout(() => {
          console.log('App: Authentication detected - reloading user language preference');
          reloadUserLanguage();
        }, 1500);
      }
    };

    checkAndLoadUserLanguage();
  }, [isAuthenticated, reloadUserLanguage]);

  const handleLogout = async () => {
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('prosperMindToken');
    delete axios.defaults.headers.common['Authorization'];
    logout();
    setNeedsDiagnosis(false);
    toast.success(t('budgetModule.loggedOutSuccess'));
  };

  const handleDiagnosisComplete = () => {
    setNeedsDiagnosis(false);
  };

  // Show auth loading screen
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading ProsperMind...</p>
        </div>
      </div>
    );
  }

  // Show loading screen while checking authentication status
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-slate-300">Loading ProsperMind...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<SimpleLoginComponent />} />
          <Route path="/preview" element={<MoneyCyclePreview />} />
          
          {/* Protected Routes */}
          <Route 
            path="/dashboard" 
            element={
              isAuthenticated ? (
                needsDiagnosis ? (
                  <DiagnosisFlow onComplete={handleDiagnosisComplete} />
                ) : (
                  <Dashboard user={user} onLogout={handleLogout} setNeedsDiagnosis={setNeedsDiagnosis} />
                )
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          
          {/* Family Invite Join Page */}
          <Route 
            path="/join/:inviteCode" 
            element={
              <JoinHouseholdPageWrapper 
                isAuthenticated={isAuthenticated}
                token={sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken')}
              />
            }
          />
          
          {/* Stripe Subscription Routes */}
          <Route path="/subscription/success" element={<SubscriptionSuccess />} />
          <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
          
          {/* Legal Pages */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          
          {/* Redirect any other routes to home */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
};

// Wrapped App with Simple Auth Provider
const AppWithAuth = () => {
  return (
    <SimpleAuthProvider>
      <LanguageProvider>
        <CurrencyProvider>
          <SubscriptionProvider>
            <ActionFeedbackProvider>
              <Toaster 
                position="top-center"
                toastOptions={{
                  duration: 3000,
                  style: {
                    background: 'linear-gradient(135deg, #065f46 0%, #047857 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '16px 24px',
                    fontSize: '15px',
                    fontWeight: '500',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
                    marginTop: '35vh',
                  },
                  className: 'custom-toast',
                }}
              />
              <App />
              <CookieBanner />
            </ActionFeedbackProvider>
          </SubscriptionProvider>
        </CurrencyProvider>
      </LanguageProvider>
    </SimpleAuthProvider>
  );
};

export default AppWithAuth;